/** Texts of the Asia Sweep model (the Asian range's liquidity sweep with a market structure shift): its tuning, its summary, its chart, its checks. Each key in both languages. */
export const asiasweep = {
  en: {
    "as.name": "Asia Sweep",
    "as.tune": "Tuning",
    "as.title": "Asia Sweep tuning",
    "as.about.title": "Asia Sweep – the strategy",
    "as.about.intro": "Gold 5M: the Asian session’s range is swept in the London or New York morning, price turns back into the range and breaks the last swing on the other side with a gap — the entry is the retest of that gap, against the sweep.",
    "as.about.steps": "Asian range: high and low 00:00–07:00 London.\nWindows: London 07:00–11:00 London, New York 08:00–11:00 New York (daylight saving time in each city).\nSweep (sell; buy mirrored): a bar in a window with its high more than 0.05 ATR above the Asian high.\nReturn: within 12 bars a close below the Asian high.\nMSS: after it within 12 bars a close below the last confirmed 5M swing low (fractal 2 or 3, from 24 bars before the sweep on); the MSS bar or the one after it leaves a gap (its high below the low two bars before).\nEntry: a limit at the gap’s near edge, valid 12 bars, cancelled by a close above the sweep’s high.\nStop: the sweep’s high + 0.1 ATR. Target: the Asian low; or the previous day’s low (day from 17:00 New York) when further; or 2 R. Only with the target at least 1 R away.\nFlat by 16:00 New York at the latest; one setup per side and day. ATR = ATR(14) on 5M.",
    "as.about.evidence": "Measured (round 45, gold 5M 2010 – 10/2026, bid/ask, $0.07 commission, $0.10 slippage, PREREG45): negative after costs in every setting, and already before costs. Chosen setting (target 2 R, fractal 3, London + New York): out of sample 2023–2026 −0.15 R per trade (n 438, profit factor 0.76), before costs −0.06 R; 2010–2026 −0.21 R. No edge — discarded. Shown for analysis only.",
    "as.target": "Target",
    "as.target.asia": "Asia",
    "as.target.pd": "Prev. day",
    "as.target.r2": "2 R",
    "as.fractal": "Swing fractal",
    "as.fractalBars": "{n} bars",
    "as.window": "Windows",
    "as.window.both": "Both",
    "as.window.london": "London only",
    "as.preset.1": "Standard",
    "as.preset.2": "Target 2 R",
    "as.preset.3": "London only",
    "as.frames": "5M · Asia 00–07 London",
    "as.row.asia": "Asian range",
    "as.row.prev": "Previous day",
    "as.row.window": "Window",
    "as.range": "{high} / {low}",
    "as.none": "–",
    "as.window.in.london": "London (07–11 London)",
    "as.window.in.newyork": "New York (08–11 New York)",
    "as.window.outside": "outside the windows",
    "as.zone.liquidity.short": "liquidity above the Asian high",
    "as.zone.liquidity.long": "liquidity below the Asian low",
    "as.zone.gap": "gap · entry zone",
    "as.role.active": "active",
    "as.role.next": "next",
    "as.step.sweep": "Sweep",
    "as.step.back": "Back in range",
    "as.step.mss": "MSS + gap",
    "as.step.retest": "Retest",
    "as.step.awaited": "awaited",
    "as.tip.trade": "Trade since {time}",
    "as.tip.limit": "Limit {entry} waiting until {time}",
    "as.tip.done": "Today's setup is over",
    "as.tip.wait.short": "Waiting for a sweep of the Asian high",
    "as.tip.wait.long": "Waiting for a sweep of the Asian low",
    "as.tip.run": "Sequence running — see the steps",
    "as.tip.noRange": "No Asian range today yet",
    "as.fig.limit": "Limit",
    "as.fig.entry": "Entry",
    "as.fig.stop": "Stop",
    "as.fig.target": "Target",
    "as.legend": "Steps: sweep past the Asian range’s side in a window, a close back inside within 12 bars, a close beyond the last swing with a gap within 12 more, then the limit at the gap.",
    "as.chart.asiaHigh": "Asia high",
    "as.chart.asiaLow": "Asia low",
    "as.chart.pdh": "Prev. day high",
    "as.chart.pdl": "Prev. day low",
    "as.chart.limit": "Limit {arrow}",
    "as.chart.entry": "Entry {arrow}",
    "as.chart.stop": "Stop",
    "as.chart.target": "Target",
    "as.chart.zone.long": "BUY ZONE {price} · {kind}",
    "as.chart.zone.short": "SELL ZONE {price} · {kind}",
    "as.chart.kind.liquidity": "Asia liquidity",
    "as.chart.kind.gap": "gap",
    "chk.asWindow": "Window",
    "chk.asWindow.hint": "Asia Sweep. ✓ while the bar lies in the London window (07:00–11:00 London) or — unless London only — the New York window (08:00–11:00 New York).",
    "chk.asSweep": "Sweep",
    "chk.asSweep.hint": "Asia Sweep. ✓ sell once today a bar in a window ran more than 0.05 ATR above the Asian high (✓ buy below the Asian low) and its sequence is running or led to today's setup.",
    "chk.asBack": "Back in range",
    "chk.asBack.hint": "Asia Sweep. ✓ once after the sweep, within 12 bars, a bar closed back inside the Asian range.",
    "chk.asMss": "MSS + gap",
    "chk.asMss.hint": "Asia Sweep. ✓ once after the return, within 12 bars, a close broke the last confirmed swing on the other side and the MSS bar or the one after it left a gap — today's setup.",
    "chk.asSetup": "Setup",
    "chk.asSetup.hint": "Asia Sweep. ✓ on its side while the limit at the gap waits or its trade runs.",
  },
  de: {
    "as.name": "Asien-Sweep",
    "as.tune": "Feintuning",
    "as.title": "Asien-Sweep-Feintuning",
    "as.about.title": "Asien-Sweep – die Strategie",
    "as.about.intro": "Gold 5M: Die Range der Asien-Sitzung wird am Londoner oder New Yorker Vormittag überlaufen, der Kurs kehrt in die Range zurück und bricht mit einer Lücke das letzte Swing auf der anderen Seite — der Einstieg ist der Retest dieser Lücke, gegen den Sweep.",
    "as.about.steps": "Asien-Range: Hoch und Tief 00:00–07:00 London.\nFenster: London 07:00–11:00 London, New York 08:00–11:00 New York (Sommerzeit je Stadt).\nSweep (Verkauf; Kauf gespiegelt): eine Kerze im Fenster mit Hoch mehr als 0,05 ATR über dem Asien-Hoch.\nRückkehr: innerhalb 12 Kerzen ein Schluss unter dem Asien-Hoch.\nMSS: danach innerhalb 12 Kerzen ein Schluss unter dem letzten bestätigten 5M-Swing-Tief (Fraktal 2 oder 3, ab 24 Kerzen vor dem Sweep); die MSS-Kerze oder die nach ihr lässt eine Lücke (ihr Hoch unter dem Tief zwei Kerzen davor).\nEinstieg: Limit an der nahen Kante der Lücke, gültig 12 Kerzen, ungültig bei einem Schluss über dem Sweep-Hoch.\nStop: Sweep-Hoch + 0,1 ATR. Ziel: Asien-Tief; oder das Vortagestief (Tag ab 17:00 New York), wenn es weiter liegt; oder 2 R. Nur mit Ziel mindestens 1 R entfernt.\nSpätestens 16:00 New York flach; je Seite und Tag ein Setup. ATR = ATR(14) auf 5M.",
    "as.about.evidence": "Gemessen (Runde 45, Gold 5M 2010 – 10/2026, Geld/Brief, 0,07 $ Kommission, 0,10 $ Slippage, PREREG45): in jeder Einstellung nach Kosten negativ, schon vor Kosten. Gewählte Einstellung (Ziel 2 R, Fraktal 3, London + New York): außerhalb 2023–2026 −0,15 R je Trade (n 438, Profit-Faktor 0,76), vor Kosten −0,06 R; 2010–2026 −0,21 R. Kein Vorteil – verworfen. Nur zur Analyse.",
    "as.target": "Ziel",
    "as.target.asia": "Asien",
    "as.target.pd": "Vortag",
    "as.target.r2": "2 R",
    "as.fractal": "Swing-Fraktal",
    "as.fractalBars": "{n} Kerzen",
    "as.window": "Fenster",
    "as.window.both": "Beide",
    "as.window.london": "Nur London",
    "as.preset.1": "Standard",
    "as.preset.2": "Ziel 2 R",
    "as.preset.3": "Nur London",
    "as.frames": "5M · Asien 00–07 London",
    "as.row.asia": "Asien-Range",
    "as.row.prev": "Vortag",
    "as.row.window": "Fenster",
    "as.range": "{high} / {low}",
    "as.none": "–",
    "as.window.in.london": "London (07–11 London)",
    "as.window.in.newyork": "New York (08–11 New York)",
    "as.window.outside": "außerhalb der Fenster",
    "as.zone.liquidity.short": "Liquidität über dem Asien-Hoch",
    "as.zone.liquidity.long": "Liquidität unter dem Asien-Tief",
    "as.zone.gap": "Lücke · Einstiegszone",
    "as.role.active": "aktiv",
    "as.role.next": "nächste",
    "as.step.sweep": "Sweep",
    "as.step.back": "Zurück in Range",
    "as.step.mss": "MSS + Lücke",
    "as.step.retest": "Retest",
    "as.step.awaited": "erwartet",
    "as.tip.trade": "Trade seit {time}",
    "as.tip.limit": "Limit {entry} wartet bis {time}",
    "as.tip.done": "Das heutige Setup ist vorbei",
    "as.tip.wait.short": "Wartet auf einen Sweep des Asien-Hochs",
    "as.tip.wait.long": "Wartet auf einen Sweep des Asien-Tiefs",
    "as.tip.run": "Ablauf läuft — siehe die Schritte",
    "as.tip.noRange": "Heute noch keine Asien-Range",
    "as.fig.limit": "Limit",
    "as.fig.entry": "Einstieg",
    "as.fig.stop": "Stop",
    "as.fig.target": "Ziel",
    "as.legend": "Schritte: Sweep über die Seite der Asien-Range im Fenster, innerhalb 12 Kerzen ein Schluss zurück in der Range, innerhalb 12 weiterer ein Schluss jenseits des letzten Swings mit Lücke, dann das Limit an der Lücke.",
    "as.chart.asiaHigh": "Asien-Hoch",
    "as.chart.asiaLow": "Asien-Tief",
    "as.chart.pdh": "Vortageshoch",
    "as.chart.pdl": "Vortagestief",
    "as.chart.limit": "Limit {arrow}",
    "as.chart.entry": "Einstieg {arrow}",
    "as.chart.stop": "Stop",
    "as.chart.target": "Ziel",
    "as.chart.zone.long": "BUY ZONE {price} · {kind}",
    "as.chart.zone.short": "SELL ZONE {price} · {kind}",
    "as.chart.kind.liquidity": "Asien-Liquidität",
    "as.chart.kind.gap": "Lücke",
    "chk.asWindow": "Fenster",
    "chk.asWindow.hint": "Asien-Sweep. ✓, solange die Kerze im London-Fenster (07:00–11:00 London) oder — außer bei „Nur London“ — im New-York-Fenster (08:00–11:00 New York) liegt.",
    "chk.asSweep": "Sweep",
    "chk.asSweep.hint": "Asien-Sweep. ✓ Verkauf, sobald heute eine Kerze im Fenster mehr als 0,05 ATR über das Asien-Hoch lief (✓ Kauf unter das Asien-Tief) und ihr Ablauf läuft oder zum heutigen Setup führte.",
    "chk.asBack": "Zurück in Range",
    "chk.asBack.hint": "Asien-Sweep. ✓, sobald nach dem Sweep innerhalb 12 Kerzen eine Kerze wieder in der Asien-Range schloss.",
    "chk.asMss": "MSS + Lücke",
    "chk.asMss.hint": "Asien-Sweep. ✓, sobald nach der Rückkehr innerhalb 12 Kerzen ein Schluss das letzte bestätigte Swing auf der anderen Seite brach und die MSS-Kerze oder die nach ihr eine Lücke ließ — das heutige Setup.",
    "chk.asSetup": "Setup",
    "chk.asSetup.hint": "Asien-Sweep. ✓ auf seiner Seite, solange das Limit an der Lücke wartet oder sein Trade läuft.",
  },
};
